// src/pages/Opdrachtenplanning.js
// vOPL-005
// - MKB-planbord per offerte/opdracht
// - Dagkolommen maandag t/m zondag
// - Linker kolom alleen Relatie en Referentie
// - Klik op linker kolom opent klantinformatie
// - Klik op een cel opent onderliggende procesinformatie

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import HelpButton from "../components/HelpButton";

const VERSION = "vOPL-005";

function safeText(value, fallback = "") {
  if (value === null || value === undefined) return fallback;
  return String(value);
}

function safeTrim(value) {
  return safeText(value).trim();
}

function isoDate(value) {
  const text = safeText(value).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(text) ? text : "";
}

function pad2(value) {
  return String(value).padStart(2, "0");
}

function dateToIsoLocal(date) {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

function parseIsoLocal(value) {
  const text = isoDate(value) || dateToIsoLocal(new Date());
  const [year, month, day] = text.split("-").map(Number);
  return new Date(year, month - 1, day);
}

function todayIso() {
  return dateToIsoLocal(new Date());
}

function addDays(value, days) {
  const date = parseIsoLocal(value);
  date.setDate(date.getDate() + Number(days || 0));
  return dateToIsoLocal(date);
}

function monday(value) {
  const date = parseIsoLocal(value);
  const day = date.getDay();
  date.setDate(date.getDate() + (day === 0 ? -6 : 1 - day));
  return dateToIsoLocal(date);
}

function isoWeekNumber(value) {
  const date = parseIsoLocal(value);
  const utc = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const day = utc.getUTCDay() || 7;
  utc.setUTCDate(utc.getUTCDate() + 4 - day);
  const start = new Date(Date.UTC(utc.getUTCFullYear(), 0, 1));
  return Math.ceil((((utc - start) / 86400000) + 1) / 7);
}

function dayLabel(value) {
  const labels = ["zo", "ma", "di", "wo", "do", "vr", "za"];
  return labels[parseIsoLocal(value).getDay()] || "";
}

function formatDateShort(value) {
  const text = isoDate(value);
  if (!text) return "";
  const [, month, day] = text.split("-");
  return `${day}-${month}`;
}

function formatDateNl(value) {
  const text = isoDate(value);
  if (!text) return "";
  const [year, month, day] = text.split("-");
  return `${day}-${month}-${year}`;
}

function formatDateTimeNl(value) {
  if (!value) return "";

  const raw = safeText(value).trim();
  const date = new Date(raw);

  if (Number.isNaN(date.getTime())) {
    const datePart = isoDate(raw);
    const timePart = raw.match(/(?:T|\s)(\d{2}:\d{2}(?::\d{2})?)/)?.[1] || "";
    return [formatDateNl(datePart), timePart].filter(Boolean).join(" ");
  }

  return date.toLocaleString("nl-NL", {
    timeZone: "Europe/Amsterdam",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function euro(value) {
  const number = Number(value || 0);
  if (!Number.isFinite(number)) return "€ 0,00";
  return number.toLocaleString("nl-NL", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function buildDays(startValue, weekCount) {
  const start = monday(startValue || todayIso());
  const total = Math.max(1, Number(weekCount || 3)) * 7;
  return Array.from({ length: total }, (_, index) => {
    const date = addDays(start, index);
    const day = parseIsoLocal(date).getDay();
    return {
      date,
      day_label: dayLabel(date),
      date_label: formatDateShort(date),
      is_weekend: day === 0 || day === 6,
    };
  });
}

function buildWeeks(startValue, weekCount) {
  const start = monday(startValue || todayIso());
  return Array.from({ length: Math.max(1, Number(weekCount || 3)) }, (_, index) => {
    const weekStart = addDays(start, index * 7);
    return {
      week: isoWeekNumber(weekStart),
      start_date: weekStart,
      end_date: addDays(weekStart, 6),
    };
  });
}

function buildHeaders() {
  let user = {};
  try {
    user = JSON.parse(
      sessionStorage.getItem("user") ||
      localStorage.getItem("user") ||
      "{}"
    );
  } catch {}

  const headers = {
    "Content-Type": "application/json",
    "x-tenant":
      sessionStorage.getItem("tenant") ||
      localStorage.getItem("tenant") ||
      "demo",
    "x-role": user?.role || "user",
    "x-staffiq-user-id": user?.id || "",
    "x-staffiq-user-email": user?.email || "",
    "x-staffiq-user-name": user?.display_name || user?.name || "",
    "x-company-code-ids":
      sessionStorage.getItem("staffiq_company_code_ids") ||
      localStorage.getItem("staffiq_company_code_ids") ||
      "",
  };

  const token =
    sessionStorage.getItem("token") ||
    localStorage.getItem("token") ||
    "";

  if (token) headers.Authorization = `Bearer ${token}`;

  Object.keys(headers).forEach((key) => {
    if (!headers[key]) delete headers[key];
  });

  return headers;
}

async function fetchJson(url) {
  const response = await fetch(url, { headers: buildHeaders() });
  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data?.error || `${response.status} ${response.statusText}`);
  }

  return data;
}

function approvalLabel(value) {
  const status = safeTrim(value).toLowerCase();
  if (status === "approved") return "Akkoord";
  if (status === "rejected") return "Niet akkoord";
  if (status === "viewed") return "Bekeken";
  if (status === "sent") return "Verstuurd";
  if (status === "expired") return "Verlopen";
  return "";
}

function workOrderLabel(item) {
  if (!item?.work_order_id) return "Niet gemaakt";
  const status = safeTrim(item.work_order_status).toUpperCase();
  const approval = safeTrim(item.work_approval_status).toLowerCase();
  if (status === "COMPLETED") return "Opgeleverd";
  if (approval === "approved") return "Geaccordeerd";
  if (status === "PENDING_APPROVAL") return "Ter akkoord";
  return "Aangemaakt";
}

function invoiceLabel(item) {
  if (!item?.invoice_id) return "Niet gemaakt";
  const status = safeTrim(item.invoice_status).toUpperCase();
  if (status === "PAID") return "Betaald";
  if (status === "PARTIAL") return "Deelbetaling";
  if (status === "FINAL") return "Definitief";
  return "Concept";
}

function processLabel(value) {
  const status = safeTrim(value).toUpperCase();
  if (status === "COMPLETED") return "Afgerond";
  if (status === "ACTION_REQUIRED") return "Actie nodig";
  return "Lopend";
}

function eventStyle(type, status = "") {
  const low = safeTrim(status).toLowerCase();

  // Rood is uitsluitend voor een concrete fout/actie:
  // niet akkoord, verlopen, te laat of expliciet actie nodig.
  if (
    low.includes("niet akkoord") ||
    low.includes("verlopen") ||
    low.includes("actie nodig") ||
    low.includes("te laat")
  ) {
    return { background: "#d62828", color: "#ffffff", border: "#d62828" };
  }

  // Betaald = donkergroen.
  if (type === "payment" && low.includes("betaald")) {
    return { background: "#2e7d32", color: "#ffffff", border: "#2e7d32" };
  }

  // Factuur en vervaldatum = lichtgroen, tenzij hierboven rood.
  if (type === "invoice" || type === "due") {
    return { background: "#a5d6a7", color: "#111111", border: "#81c784" };
  }

  // Werkbon / oplevering = paars.
  if (type === "work_order" || type === "completion") {
    return { background: "#7e57c2", color: "#ffffff", border: "#7e57c2" };
  }

  // Uitvoering = geel.
  if (type === "execution") {
    return { background: "#f4d35e", color: "#111111", border: "#e0b93f" };
  }

  // Opdracht = donkerblauw.
  if (type === "order") {
    return { background: "#1f4e79", color: "#ffffff", border: "#1f4e79" };
  }

  // Offerte = lichtblauw.
  return { background: "#8ecae6", color: "#111111", border: "#6faed0" };
}

function addEvent(map, date, event) {
  const key = isoDate(date);
  if (!key) return;
  if (!map[key]) map[key] = [];
  map[key].push(event);
}

function buildEvents(item) {
  const events = {};
  const offerStatus = safeTrim(item.offer_status).toUpperCase();
  const offerApproval = approvalLabel(item.offer_approval_status);
  const orderApproval = approvalLabel(item.order_approval_status);

  addEvent(events, item.offer_date, {
    type: "offer",
    short: "Off",
    title: "Offerte",
    status: offerApproval || (offerStatus === "OFFERTE" ? "Offerte" : "Aangemaakt"),
  });

  if (item.offer_approval_sent_at) {
    addEvent(events, item.offer_approval_sent_at, {
      type: "offer",
      short: "Off",
      title: "Offerte ter akkoord",
      status: offerApproval || "Verstuurd",
    });
  }

  if (item.offer_approval_approved_at) {
    addEvent(events, item.offer_approval_approved_at, {
      type: "offer",
      short: "Off",
      title: "Offerte",
      status: "Akkoord",
    });
  }

  if (offerStatus === "OPDRACHT" || offerStatus === "FACTUUR" || item.order_approval_status) {
    const orderDate =
      isoDate(item.order_approval_approved_at) ||
      isoDate(item.order_approval_sent_at) ||
      isoDate(item.updated_at) ||
      isoDate(item.offer_date);

    addEvent(events, orderDate, {
      type: "order",
      short: "Opd",
      title: "Opdracht",
      status: orderApproval || (offerStatus === "FACTUUR" ? "Akkoord" : "Opdracht"),
    });
  }

  if (item.execution_date) {
    addEvent(events, item.execution_date, {
      type: "execution",
      short: "Uitv",
      title: "Uitvoering",
      status: [item.start_time, item.executed_by].filter(Boolean).join(" · ") || "Gepland",
    });
  }

  if (item.work_order_id) {
    addEvent(events, item.execution_date || item.completion_date || item.updated_at, {
      type: "work_order",
      short: "Werk",
      title: "Werkbon",
      status: workOrderLabel(item),
    });
  }

  if (item.completion_date) {
    addEvent(events, item.completion_date, {
      type: "completion",
      short: "Opl",
      title: "Oplevering",
      status: workOrderLabel(item),
    });
  }

  if (item.invoice_id && item.invoice_date) {
    addEvent(events, item.invoice_date, {
      type: "invoice",
      short: "Fact",
      title: `Factuur ${item.invoice_number || ""}`.trim(),
      status: invoiceLabel(item),
    });
  }

  if (item.invoice_id && item.due_date) {
    const overdue =
      item.due_date < todayIso() && Number(item.outstanding_amount || 0) > 0.005;

    addEvent(events, item.due_date, {
      type: "due",
      short: "Verv",
      title: "Vervaldatum",
      status: overdue ? "Te laat" : `Open ${euro(item.outstanding_amount)}`,
    });
  }

  if (item.last_payment_date || safeTrim(item.payment_status).toLowerCase() === "betaald") {
    addEvent(events, item.last_payment_date || item.due_date || item.invoice_date, {
      type: "payment",
      short: "Bet",
      title: "Betaling",
      status: item.payment_status || "Betaald",
    });
  }

  return events;
}

function InfoRow({ label, value }) {
  const text = safeTrim(value);
  if (!text) return null;

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "145px 1fr",
        gap: 10,
        padding: "5px 0",
        borderBottom: "1px solid #f1f1f1",
      }}
    >
      <div style={{ fontWeight: 700, color: "#444" }}>{label}</div>
      <div style={{ whiteSpace: "pre-wrap" }}>{text}</div>
    </div>
  );
}

function Modal({ title, subtitle, children, onClose }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 3000,
        background: "rgba(0,0,0,0.42)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 18,
        boxSizing: "border-box",
      }}
    >
      <div
        onClick={(event) => event.stopPropagation()}
        style={{
          width: "min(980px, 96vw)",
          maxHeight: "92vh",
          overflow: "hidden",
          background: "white",
          borderRadius: 12,
          boxShadow: "0 20px 60px rgba(0,0,0,0.28)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            padding: "14px 16px",
            borderBottom: "1px solid #e6e6e6",
            display: "grid",
            gridTemplateColumns: "1fr auto",
            gap: 12,
            alignItems: "center",
          }}
        >
          <div>
            <h2 style={{ margin: 0, fontSize: 20 }}>{title}</h2>
            {subtitle && (
              <div style={{ fontSize: 13, opacity: 0.7, marginTop: 3 }}>
                {subtitle}
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={onClose}
            style={{
              border: "none",
              background: "#1f4e79",
              color: "white",
              borderRadius: 6,
              padding: "9px 14px",
              cursor: "pointer",
              fontWeight: 700,
            }}
          >
            Sluiten
          </button>
        </div>

        <div style={{ padding: 16, overflowY: "auto" }}>{children}</div>
      </div>
    </div>
  );
}

export default function Opdrachtenplanning() {
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState(monday(todayIso()));
  const [weeks, setWeeks] = useState(3);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [cellPopup, setCellPopup] = useState(null);
  const [relationPopup, setRelationPopup] = useState(null);
  const [relationLoading, setRelationLoading] = useState(false);
  const [relationError, setRelationError] = useState("");
  const [hoverInfo, setHoverInfo] = useState(null);
  const boardScrollRef = useRef(null);
  const searchRef = useRef(null);

  async function loadItems() {
    try {
      setLoading(true);
      setError("");
      const data = await fetchJson("/api/order-process-overview");
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      setItems([]);
      setError(err?.message || "Opdrachtenplanning laden mislukt.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadItems();
  }, []);

  useEffect(() => {
    function handleScopeChange() {
      loadItems();
    }

    window.addEventListener("staffiq-company-scope-changed", handleScopeChange);
    return () => {
      window.removeEventListener("staffiq-company-scope-changed", handleScopeChange);
    };
  }, []);

  const days = useMemo(() => buildDays(startDate, weeks), [startDate, weeks]);
  const weekGroups = useMemo(() => buildWeeks(startDate, weeks), [startDate, weeks]);
  const visibleStart = days[0]?.date || startDate;
  const visibleEnd = days[days.length - 1]?.date || addDays(startDate, weeks * 7 - 1);

  const rows = useMemo(() => {
    const query = safeTrim(q).toLowerCase();

    return items
      .map((item) => ({ ...item, events: buildEvents(item) }))
      .filter((item) => {
        const process = safeTrim(item.process_status).toUpperCase();
        if (!showAll && process === "COMPLETED") return false;
        if (statusFilter && process !== statusFilter) return false;

        if (query) {
          const haystack = [
            item.organisation_name,
            item.offer_number,
            item.reference,
            item.contact_name,
            item.company_code_name,
            item.company_code,
          ]
            .join(" ")
            .toLowerCase();

          if (!haystack.includes(query)) return false;
        }

        return true;
      })
      .sort((a, b) => {
        const relationCompare = safeTrim(a.organisation_name).localeCompare(
          safeTrim(b.organisation_name),
          "nl"
        );
        if (relationCompare !== 0) return relationCompare;
        return safeTrim(a.offer_number).localeCompare(safeTrim(b.offer_number), "nl");
      });
  }, [items, q, showAll, statusFilter]);

  const counts = useMemo(
    () => ({
      running: items.filter((item) => safeTrim(item.process_status).toUpperCase() === "RUNNING").length,
      action: items.filter((item) => safeTrim(item.process_status).toUpperCase() === "ACTION_REQUIRED").length,
      completed: items.filter((item) => safeTrim(item.process_status).toUpperCase() === "COMPLETED").length,
    }),
    [items]
  );

  function prevPeriod() {
    setStartDate((value) => monday(addDays(value, -7)));
  }

  function nextPeriod() {
    setStartDate((value) => monday(addDays(value, 7)));
  }

  function goToday() {
    setStartDate(monday(todayIso()));
  }

  function refreshBoard() {
    setQ("");
    setShowAll(false);
    setStatusFilter("");
    setStartDate(monday(todayIso()));
    setCellPopup(null);
    setRelationPopup(null);
    loadItems();

    try {
      boardScrollRef.current?.scrollTo?.({ top: 0, left: 0, behavior: "auto" });
    } catch {}

    if (window.innerWidth > 768) {
      setTimeout(() => {
        try {
          searchRef.current?.focus?.();
        } catch {}
      }, 0);
    }
  }

  async function openRelation(item) {
    if (!item?.organisation_id) return;

    setRelationPopup({ item, data: null });
    setRelationLoading(true);
    setRelationError("");

    try {
      const data = await fetchJson(`/api/organisations/${encodeURIComponent(item.organisation_id)}`);
      setRelationPopup({ item, data: data || {} });
    } catch (err) {
      setRelationError(err?.message || "Klantinformatie laden mislukt.");
    } finally {
      setRelationLoading(false);
    }
  }

  function openOrder(item) {
    if (!item?.offer_id) return;
    try {
      sessionStorage.setItem("staffiq_reopen_offer_id", String(item.offer_id));
    } catch {}
    navigate("/offertes");
  }

  function openWorkOrder(item) {
    if (!item?.offer_id || !item?.work_order_id) return;
    navigate(`/werkbon-oplevering/${item.offer_id}`);
  }

  function openInvoice(item) {
    if (!item?.invoice_id) return;
    try {
      sessionStorage.setItem("staffiq_reopen_invoice_id", String(item.invoice_id));
    } catch {}
    navigate("/facturen");
  }

  const isMobile = window.innerWidth <= 768;
  const LEFT_W = isMobile ? 180 : 205;
  const CELL_W = isMobile ? 70 : 78;
  const DAY_H = 28;
  const ROW_H = 44;

  const buttonStyle = {
    padding: "7px 12px",
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    cursor: "pointer",
    whiteSpace: "nowrap",
  };

  const activeButtonStyle = {
    ...buttonStyle,
    background: "#1f4e79",
    color: "white",
  };

  const inputStyle = {
    padding: "7px 10px",
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    boxSizing: "border-box",
  };

  const countBadgeStyle = {
    minWidth: 38,
    height: 24,
    padding: "0 8px",
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "#f7fafc",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    fontWeight: 700,
    color: "#1f4e79",
  };

  function StatusButton({ value, label, count }) {
    const active = statusFilter === value;

    function selectStatus() {
      if (active) {
        setStatusFilter("");
        setShowAll(false);
        return;
      }

      setStatusFilter(value);
      setShowAll(value === "COMPLETED");
    }

    return (
      <button
        type="button"
        onClick={selectStatus}
        style={active ? activeButtonStyle : buttonStyle}
      >
        {label}: <b>{count}</b>
      </button>
    );
  }

  function cellTooltipText(item, day, events) {
    const lines = [
      `${item.organisation_name || "-"} · ${item.offer_number || item.reference || item.offer_id}`,
      `${day.day_label || ""} ${formatDateNl(day.date)}`.trim(),
      "",
    ];

    if (!events.length) {
      lines.push("Geen gebeurtenis op deze datum.");
    } else {
      events.forEach((event) => {
        lines.push(`${event.title}: ${event.status || "-"}`);
      });
    }

    lines.push("");
    lines.push(`Proces: ${processLabel(item.process_status)}`);
    lines.push(`Offerte: ${approvalLabel(item.offer_approval_status) || safeTrim(item.offer_status) || "-"}`);
    lines.push(`Opdracht: ${approvalLabel(item.order_approval_status) || "-"}`);
    lines.push(`Werkbon: ${workOrderLabel(item)}`);
    lines.push(`Factuur: ${item.invoice_id ? `${item.invoice_number || ""} ${invoiceLabel(item)}`.trim() : "Niet gemaakt"}`);
    if (item.invoice_id) lines.push(`Openstaand: ${euro(item.outstanding_amount)}`);

    return lines.join("\n");
  }

  function renderCell(item, day) {
    const events = Array.isArray(item.events?.[day.date]) ? item.events[day.date] : [];
    if (!events.length) return null;

    const first = events[0];
    const style = eventStyle(first.type, first.status);

    let label = "";
    if (events.length === 1) {
      label = `${first.short} ${first.status}`;
    } else if (events.length === 2) {
      label = `${events[0].short} + ${events[1].short}`;
    } else {
      label = `${events.length} acties`;
    }

    return {
      label,
      style,
      tooltip: cellTooltipText(item, day, events),
    };
  }


  return (
    <div style={{ width: "100%", minWidth: 0 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginTop: 0,
          marginBottom: 10,
        }}
      >
        <h1 style={{ margin: 0 }}>
          Opdrachtenplanning{" "}
          <span style={{ fontSize: 12, opacity: 0.5 }}>{VERSION}</span>
        </h1>

        <HelpButton
          topic="opdrachtenplanning"
          title="Help over Opdrachtenplanning"
        />
      </div>

      <div
        style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
          flexWrap: "wrap",
          marginBottom: 8,
        }}
      >
        <button onClick={prevPeriod} style={buttonStyle}>◀ Week terug</button>
        <button onClick={goToday} style={buttonStyle}>Vandaag</button>
        <button onClick={nextPeriod} style={buttonStyle}>Week vooruit ▶</button>

        <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <span style={{ fontSize: 13 }}>Start</span>
          <input
            type="date"
            value={startDate}
            onChange={(event) => setStartDate(monday(event.target.value))}
            style={inputStyle}
          />
        </label>

        <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <span style={{ fontSize: 13 }}>Weken</span>
          <select
            value={weeks}
            onChange={(event) => setWeeks(Number(event.target.value) || 3)}
            style={inputStyle}
          >
            {[3, 4, 5, 6, 8, 10, 12].map((value) => (
              <option key={value} value={value}>{value}</option>
            ))}
          </select>
        </label>

        <input
          ref={searchRef}
          value={q}
          onChange={(event) => setQ(event.target.value)}
          placeholder="Zoek..."
          autoComplete="off"
          style={{ ...inputStyle, minWidth: 180 }}
        />
      </div>

      <div
        style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
          flexWrap: "wrap",
          marginBottom: 8,
        }}
      >
        <StatusButton value="RUNNING" label="Lopend" count={counts.running} />
        <StatusButton value="ACTION_REQUIRED" label="Actie nodig" count={counts.action} />
        <StatusButton value="COMPLETED" label="Afgerond" count={counts.completed} />

        <button
          type="button"
          onClick={() => {
            setShowAll((previous) => !previous);
            setStatusFilter("");
          }}
          style={showAll && !statusFilter ? activeButtonStyle : buttonStyle}
        >
          Alles
        </button>

        <button type="button" onClick={refreshBoard} style={buttonStyle}>
          🔄 Vernieuw
        </button>

        <span style={{ fontSize: 12, color: "#666" }}>
          Standaard worden lopende opdrachten en opdrachten met actie getoond.
        </span>
      </div>

      <div
        style={{
          display: "flex",
          gap: 10,
          flexWrap: "wrap",
          marginBottom: 8,
          fontSize: 12,
        }}
      >
        {[
          ["Offerte", eventStyle("offer")],
          ["Opdracht", eventStyle("order")],
          ["Uitvoering", eventStyle("execution")],
          ["Werkbon/Oplevering", eventStyle("work_order")],
          ["Factuur/Vervaldatum", eventStyle("invoice")],
          ["Betaling", eventStyle("payment", "Betaald")],
          ["Actie nodig", eventStyle("offer", "Actie nodig")],
        ].map(([label, style]) => (
          <span key={label} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span
              style={{
                width: 16,
                height: 16,
                border: `1px solid ${style.border}`,
                background: style.background,
                display: "inline-block",
              }}
            />
            {label}
          </span>
        ))}
      </div>

      {error && <div style={{ color: "#b00020", marginBottom: 8 }}>{error}</div>}
      {loading && <div style={{ marginBottom: 8 }}>Opdrachtenplanning wordt geladen...</div>}

      <div style={{ minWidth: 0 }}>
        <div
          ref={boardScrollRef}
          style={{
            border: "1px solid #d9d9d9",
            borderRadius: 6,
            background: "white",
            overflow: "auto",
            maxHeight: "calc(100dvh - 195px)",
          }}
        >
          <div style={{ minWidth: LEFT_W + days.length * CELL_W }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: `${LEFT_W}px repeat(${days.length}, ${CELL_W}px)`,
                position: "sticky",
                top: 0,
                zIndex: 5,
              }}
            >
              <div
                style={{
                  gridRow: "1 / span 2",
                  position: "sticky",
                  left: 0,
                  zIndex: 8,
                  background: "#f7f7f7",
                  borderRight: "1px solid #cfcfcf",
                  borderBottom: "1px solid #cfcfcf",
                  padding: "8px 10px",
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <span>Relatie / Referentie</span>
                <span style={countBadgeStyle}>{rows.length}</span>
              </div>

              {weekGroups.map((week, index) => (
                <div
                  key={`${week.week}-${index}`}
                  style={{
                    gridColumn: `${2 + index * 7} / span 7`,
                    background: "#ececec",
                    borderRight: "1px solid #cfcfcf",
                    borderBottom: "1px solid #cfcfcf",
                    textAlign: "center",
                    fontWeight: 700,
                    fontSize: 12,
                    height: DAY_H,
                    lineHeight: `${DAY_H}px`,
                  }}
                >
                  Week {week.week} &nbsp; {formatDateShort(week.start_date)} t/m {formatDateShort(week.end_date)}
                </div>
              ))}

              {days.map((day) => (
                <div
                  key={day.date}
                  style={{
                    background: day.is_weekend ? "#f4f4f4" : "#fff",
                    borderRight: "1px solid #e1e1e1",
                    borderBottom: "1px solid #cfcfcf",
                    textAlign: "center",
                    fontSize: 11,
                    height: DAY_H,
                    lineHeight: "1.15",
                    paddingTop: 3,
                    boxSizing: "border-box",
                  }}
                >
                  <div>{day.day_label}</div>
                  <div>{day.date_label}</div>
                </div>
              ))}
            </div>

            {rows.length === 0 ? (
              <div style={{ padding: 14, color: "#666" }}>Geen opdrachten gevonden.</div>
            ) : (
              rows.map((item) => (
                <div
                  key={item.offer_id}
                  style={{
                    display: "grid",
                    gridTemplateColumns: `${LEFT_W}px repeat(${days.length}, ${CELL_W}px)`,
                    minHeight: ROW_H,
                  }}
                >
                  <button
                    type="button"
                    onClick={() => openRelation(item)}
                    title={`Klantinformatie openen: ${item.organisation_name || ""}`}
                    style={{
                      position: "sticky",
                      left: 0,
                      zIndex: 3,
                      background: "white",
                      border: "none",
                      borderRight: "1px solid #cfcfcf",
                      borderBottom: "1px solid #efefef",
                      padding: "5px 8px",
                      boxSizing: "border-box",
                      overflow: "hidden",
                      textAlign: "left",
                      cursor: "pointer",
                      fontFamily: "inherit",
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 700,
                        fontSize: 13,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        color: "#111",
                      }}
                    >
                      {item.organisation_name || "-"}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: "#666",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {item.offer_number || item.reference || item.offer_id}
                    </div>
                  </button>

                  {days.map((day) => {
                    const events = Array.isArray(item.events?.[day.date])
                      ? item.events[day.date]
                      : [];

                    const rendered = renderCell(item, day);
                    const cellBackground = rendered?.style?.background || (day.is_weekend ? "#f4f4f4" : "#fff");
                    const cellColor = rendered?.style?.color || "#111";

                    return (
                      <button
                        key={`${item.offer_id}-${day.date}`}
                        type="button"
                        onClick={() => setCellPopup({ item, day, events })}
                        onMouseEnter={(event) => {
                          if (!rendered) return;
                          const rect = event.currentTarget.getBoundingClientRect();
                          setHoverInfo({
                            text: rendered.tooltip,
                            x: Math.min(rect.left, window.innerWidth - 390),
                            y: Math.min(rect.bottom + 6, window.innerHeight - 260),
                          });
                        }}
                        onMouseLeave={() => setHoverInfo(null)}
                        onFocus={(event) => {
                          if (!rendered) return;
                          const rect = event.currentTarget.getBoundingClientRect();
                          setHoverInfo({
                            text: rendered.tooltip,
                            x: Math.min(rect.left, window.innerWidth - 390),
                            y: Math.min(rect.bottom + 6, window.innerHeight - 260),
                          });
                        }}
                        onBlur={() => setHoverInfo(null)}
                        style={{
                          height: ROW_H,
                          border: "1px solid #e1e1e1",
                          background: cellBackground,
                          color: cellColor,
                          fontSize: 10,
                          padding: "2px 4px",
                          cursor: "pointer",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          boxSizing: "border-box",
                          fontWeight: rendered ? 700 : 400,
                        }}
                      >
                        {rendered?.label || ""}
                      </button>
                    );
                  })}
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {hoverInfo && (
        <div
          style={{
            position: "fixed",
            left: Math.max(12, hoverInfo.x),
            top: Math.max(12, hoverInfo.y),
            zIndex: 5000,
            width: "min(370px, calc(100vw - 24px))",
            padding: "12px 14px",
            borderRadius: 8,
            background: "#111",
            color: "#fff",
            boxShadow: "0 10px 28px rgba(0,0,0,0.30)",
            fontSize: 13,
            lineHeight: 1.45,
            whiteSpace: "pre-wrap",
            pointerEvents: "none",
          }}
        >
          {hoverInfo.text}
        </div>
      )}

      {cellPopup && (
        <Modal
          title="Procesinformatie"
          subtitle={`${cellPopup.item.organisation_name || ""} · ${cellPopup.item.offer_number || cellPopup.item.offer_id} · ${formatDateNl(cellPopup.day.date)}`}
          onClose={() => setCellPopup(null)}
        >
          <div style={{ display: "grid", gap: 12 }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr",
                gap: 12,
              }}
            >
              <div style={{ border: "1px solid #e5e5e5", borderRadius: 8, padding: 10, background: "#fafafa" }}>
                <b>Relatie</b><br />
                {cellPopup.item.organisation_name || "-"}
              </div>
              <div style={{ border: "1px solid #e5e5e5", borderRadius: 8, padding: 10, background: "#fafafa" }}>
                <b>Referentie</b><br />
                {cellPopup.item.offer_number || cellPopup.item.reference || cellPopup.item.offer_id}
              </div>
              <div style={{ border: "1px solid #e5e5e5", borderRadius: 8, padding: 10, background: "#fafafa" }}>
                <b>Proces</b><br />
                {processLabel(cellPopup.item.process_status)}
              </div>
            </div>

            {cellPopup.events.length ? (
              <div style={{ display: "grid", gap: 8 }}>
                {cellPopup.events.map((event, index) => {
                  const style = eventStyle(event.type, event.status);
                  return (
                    <div
                      key={`${event.type}-${index}`}
                      style={{
                        border: `1px solid ${style.border}`,
                        background: style.background,
                        color: style.color,
                        borderRadius: 6,
                        padding: "6px 10px",
                        minHeight: 0,
                        lineHeight: 1.25,
                      }}
                    >
                      <b>{event.title}</b> - {event.status || "-"}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ color: "#666", border: "1px solid #e5e5e5", borderRadius: 8, padding: 12 }}>
                Geen gebeurtenis op deze datum.
              </div>
            )}

            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                gap: 14,
              }}
            >
              <div style={{ border: "1px solid #e5e5e5", borderRadius: 8, padding: 12 }}>
                <h3 style={{ marginTop: 0 }}>Offerte en opdracht</h3>
                <InfoRow label="Aangemaakt" value={formatDateTimeNl(cellPopup.item.created_at)} />
                <InfoRow label="Offertedatum" value={formatDateNl(cellPopup.item.offer_date)} />
                <InfoRow label="Offertestatus" value={safeTrim(cellPopup.item.offer_status)} />
                <InfoRow label="Offerte akkoordstatus" value={approvalLabel(cellPopup.item.offer_approval_status)} />
                <InfoRow label="Offerte verzonden" value={formatDateTimeNl(cellPopup.item.offer_approval_sent_at)} />
                <InfoRow label="Offerte bekeken" value={safeTrim(cellPopup.item.offer_approval_status).toLowerCase() === "viewed" ? "Ja" : ""} />
                <InfoRow label="Offerte goedgekeurd" value={formatDateTimeNl(cellPopup.item.offer_approval_approved_at)} />
                <InfoRow label="Offerte afgewezen" value={formatDateTimeNl(cellPopup.item.offer_approval_rejected_at)} />
                <InfoRow label="Opdracht akkoordstatus" value={approvalLabel(cellPopup.item.order_approval_status) || (safeTrim(cellPopup.item.offer_status).toUpperCase() === "FACTUUR" ? "Akkoord" : "")} />
                <InfoRow label="Opdracht verzonden" value={formatDateTimeNl(cellPopup.item.order_approval_sent_at)} />
                <InfoRow label="Opdracht goedgekeurd" value={formatDateTimeNl(cellPopup.item.order_approval_approved_at)} />
                <InfoRow label="Opdracht afgewezen" value={formatDateTimeNl(cellPopup.item.order_approval_rejected_at)} />
                <InfoRow label="Laatste wijziging" value={formatDateTimeNl(cellPopup.item.updated_at)} />
              </div>

              <div style={{ border: "1px solid #e5e5e5", borderRadius: 8, padding: 12 }}>
                <h3 style={{ marginTop: 0 }}>Uitvoering en werkbon</h3>
                <InfoRow label="Uitvoeringsdatum" value={formatDateNl(cellPopup.item.execution_date)} />
                <InfoRow label="Aanvangstijd" value={cellPopup.item.start_time} />
                <InfoRow label="Uitgevoerd door" value={cellPopup.item.executed_by} />
                <InfoRow label="Werkbonstatus" value={workOrderLabel(cellPopup.item)} />
                <InfoRow label="Werkbon verzonden" value={formatDateTimeNl(cellPopup.item.work_approval_sent_at)} />
                <InfoRow label="Werkbon geaccordeerd" value={formatDateTimeNl(cellPopup.item.work_approval_approved_at)} />
                <InfoRow label="Werkbon afgewezen" value={formatDateTimeNl(cellPopup.item.work_approval_rejected_at)} />
                <InfoRow label="Opleverdatum" value={formatDateNl(cellPopup.item.completion_date)} />
                <InfoRow label="Akkoordgever" value={cellPopup.item.completion_approved_name} />
              </div>

              <div style={{ border: "1px solid #e5e5e5", borderRadius: 8, padding: 12 }}>
                <h3 style={{ marginTop: 0 }}>Factuur en betaling</h3>
                <InfoRow label="Factuurnummer" value={cellPopup.item.invoice_number} />
                <InfoRow label="Factuurdatum" value={formatDateNl(cellPopup.item.invoice_date)} />
                <InfoRow label="Factuurstatus" value={invoiceLabel(cellPopup.item)} />
                <InfoRow label="Vervaldatum" value={formatDateNl(cellPopup.item.due_date)} />
                <InfoRow label="Factuurbedrag" value={cellPopup.item.invoice_id ? euro(cellPopup.item.invoice_total) : ""} />
                <InfoRow label="Betaald" value={cellPopup.item.invoice_id ? euro(cellPopup.item.paid_total) : ""} />
                <InfoRow label="Laatste betaling" value={formatDateNl(cellPopup.item.last_payment_date)} />
                <InfoRow label="Openstaand" value={cellPopup.item.invoice_id ? euro(cellPopup.item.outstanding_amount) : ""} />
                <InfoRow label="Betaalstatus" value={cellPopup.item.payment_status} />
              </div>

              <div style={{ border: "1px solid #e5e5e5", borderRadius: 8, padding: 12 }}>
                <h3 style={{ marginTop: 0 }}>Algemeen</h3>
                <InfoRow label="Relatie" value={cellPopup.item.organisation_name} />
                <InfoRow label="Contactpersoon" value={cellPopup.item.contact_name} />
                <InfoRow label="Referentie" value={cellPopup.item.offer_number || cellPopup.item.reference} />
                <InfoRow label="Opdrachtbedrag" value={euro(cellPopup.item.order_total)} />
                <InfoRow label="Processtatus" value={processLabel(cellPopup.item.process_status)} />
                <InfoRow label="Bedrijfscode" value={cellPopup.item.company_code_name || cellPopup.item.company_code} />
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, flexWrap: "wrap" }}>
              <button type="button" onClick={() => openOrder(cellPopup.item)} style={buttonStyle}>Open opdracht</button>
              <button
                type="button"
                onClick={() => openWorkOrder(cellPopup.item)}
                disabled={!cellPopup.item.work_order_id}
                style={{ ...buttonStyle, opacity: cellPopup.item.work_order_id ? 1 : 0.55, cursor: cellPopup.item.work_order_id ? "pointer" : "not-allowed" }}
              >
                Open werkbon
              </button>
              <button
                type="button"
                onClick={() => openInvoice(cellPopup.item)}
                disabled={!cellPopup.item.invoice_id}
                style={{ ...buttonStyle, opacity: cellPopup.item.invoice_id ? 1 : 0.55, cursor: cellPopup.item.invoice_id ? "pointer" : "not-allowed" }}
              >
                Open factuur
              </button>
            </div>
          </div>
        </Modal>
      )}

      {relationPopup && (
        <Modal
          title="Klantinformatie"
          subtitle={`${relationPopup.item.organisation_name || ""} · ${relationPopup.item.offer_number || relationPopup.item.offer_id}`}
          onClose={() => setRelationPopup(null)}
        >
          {relationLoading && <div style={{ marginBottom: 10 }}>Klantinformatie wordt geladen...</div>}
          {relationError && <div style={{ color: "crimson", marginBottom: 10 }}>{relationError}</div>}

          {(() => {
            const data = relationPopup.data || {};
            return (
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap: 14 }}>
                <div>
                  <h3>Algemeen</h3>
                  <InfoRow label="Naam" value={data.name || relationPopup.item.organisation_name} />
                  <InfoRow label="Zoeknaam" value={data.search_name} />
                  <InfoRow label="Soort relatie" value={data.type} />
                  <InfoRow label="Bedrijfscode" value={data.company_code_name || relationPopup.item.company_code_name || relationPopup.item.company_code} />
                </div>
                <div>
                  <h3>Contact</h3>
                  <InfoRow label="Contactpersoon" value={relationPopup.item.contact_name} />
                  <InfoRow label="Telefoon" value={data.phone} />
                  <InfoRow label="Mobiel" value={data.mobile} />
                  <InfoRow label="E-mail" value={data.email} />
                  <InfoRow label="Website" value={data.website} />
                </div>
                <div>
                  <h3>Adres / opdracht</h3>
                  <InfoRow label="Bezoekadres" value={[data.visit_street, data.visit_house_number, data.visit_addition, data.visit_postcode, data.visit_city, data.visit_country].filter(Boolean).join(" ")} />
                  <InfoRow label="Referentie" value={relationPopup.item.offer_number || relationPopup.item.reference} />
                  <InfoRow label="Bedrag" value={euro(relationPopup.item.order_total)} />
                  <InfoRow label="Proces" value={processLabel(relationPopup.item.process_status)} />
                </div>
              </div>
            );
          })()}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16, flexWrap: "wrap" }}>
            <button type="button" onClick={() => openOrder(relationPopup.item)} style={buttonStyle}>Open opdracht</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
